import { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    Radio,
    Pagination,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { encryptData, decryptData } from '../ResponseHandlers/indexedDBHelper';
import CachedImage from '../components/CachedImage';

const IndexedDBUtils = {
    async openDB() {
        return new Promise(async (resolve, reject) => {
            const request = indexedDB.open('QuestionDB', 1);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('starredQuestions')) {
                    db.createObjectStore('starredQuestions', { keyPath: 'id' });
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    },

    async addStarredQuestion(question) {
        const db = await this.openDB();
        const transaction = db.transaction(['starredQuestions'], 'readwrite');
        const store = transaction.objectStore('starredQuestions');
        const encryptedValue = encryptData(question);
        var newItem = { id: question.id, value: encryptedValue };
        await store.put(newItem);

        await transaction.done;
        // return store.put(question);
    },

    async removeStarredQuestion(questionId) {
        const db = await this.openDB();
        const transaction = db.transaction(['starredQuestions'], 'readwrite');
        const store = transaction.objectStore('starredQuestions');
        return store.delete(questionId);
    },

    async getstarredQuestions() {
        const db = await this.openDB();
        const transaction = db.transaction(['starredQuestions'], 'readonly');
        const store = transaction.objectStore('starredQuestions');
        const items = await store.getAll();
        // return items.map((item) => ({
        //     id: item.id,
        //     value: decryptData(item.value),
        // }));
        return items.map((item) => decryptData(item.value));
    },
};

const TestCard = ({ question, onAnswerSelect, selectedAnswer }) => {
    const [isStarred, setIsStarred] = useState(false);
    // const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
        const checkStarStatus = async () => {
            const db = await IndexedDBUtils.openDB();
            const transaction = db.transaction(
                ['starredQuestions'],
                'readonly',
            );
            const store = transaction.objectStore('starredQuestions');
            const request = store.get(question.id);

            request.onsuccess = () => {
                setIsStarred(!!request.result);
            };
        };
        checkStarStatus();
    }, [question.id]);

    const handleStarToggle = async () => {
        // const newStarredState = forceAdd !== undefined ? forceAdd : !isStarred;
        const newStarredState = !isStarred;
        setIsStarred(newStarredState);

        // Add or remove from IndexedDB based on star state
        if (newStarredState) {
            await IndexedDBUtils.addStarredQuestion(question);
        } else {
            await IndexedDBUtils.removeStarredQuestion(question.id);
        }
    };

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="left"
                    mb={2}
                >
                    <Typography align="left" variant="h6">
                        {question.questionBody}
                    </Typography>
                    <Button
                        onClick={() => handleStarToggle()}
                        color={isStarred ? 'warning' : 'default'}
                    >
                        {isStarred ? <StarIcon /> : <StarBorderIcon />}
                    </Button>
                </Box>
                {question.imagePath !== '-' && (
                    // <Box mb={2} display="flex" justifyContent="center">
                    //     <img
                    //         src={'/resources/' + question.imagePath + '.png'}
                    //         alt="Question visual"
                    //         style={{ maxWidth: '100%', maxHeight: '300px' }}
                    //     />
                    // </Box>
                    <CachedImage imagePath={question.imagePath} />
                )}
                <RadioGroup
                    value={selectedAnswer || ''}
                    onChange={(e) => {
                        const selectedChoice = e.target.value;
                        onAnswerSelect(selectedChoice);

                        if (selectedChoice !== question.answer) {
                            // Check if not already starred
                            const checkAndMarkQuestion = async () => {
                                try {
                                    // Check if the question is already in starred questions
                                    const db = await IndexedDBUtils.openDB();
                                    const transaction = db.transaction(
                                        ['starredQuestions'],
                                        'readonly',
                                    );
                                    const store =
                                        transaction.objectStore(
                                            'starredQuestions',
                                        );
                                    const request = store.get(question.id);

                                    request.onsuccess = async () => {
                                        // If not already starred, add to IndexedDB
                                        if (!request.result) {
                                            await IndexedDBUtils.addStarredQuestion(
                                                {
                                                    ...question,
                                                    autoStarred: true,
                                                },
                                            );
                                            // Optionally update UI state if needed
                                            setIsStarred(true);
                                        }
                                    };
                                } catch (error) {
                                    console.error(
                                        'Error checking/marking question:',
                                        error,
                                    );
                                }
                            };

                            checkAndMarkQuestion();
                        }
                    }}
                >
                    {['A', 'B', 'C', 'D'].map((choice) => (
                        <FormControlLabel
                            key={choice}
                            value={choice}
                            align="left"
                            control={
                                <Radio
                                    sx={{
                                        color:
                                            selectedAnswer &&
                                            (choice === question.answer
                                                ? 'limegreen'
                                                : choice === selectedAnswer
                                                  ? 'red'
                                                  : 'default'),
                                    }}
                                />
                            }
                            label={question[`choice${choice}`]}
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    color:
                                        selectedAnswer &&
                                        (choice === question.answer
                                            ? 'limegreen'
                                            : choice === selectedAnswer
                                              ? 'red'
                                              : 'default'),
                                },
                            }}
                            // disabled={selectedAnswer !== null}
                        />
                    ))}
                </RadioGroup>
            </CardContent>
        </Card>
    );
};

const FragenPage = ({ questions }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAnswers, setSelectedAnswers] = useState({});

    useEffect(() => {
        const mainElement = document.querySelector('main.MuiBox-root');
        if (mainElement) {
            mainElement.id = 'app-main';
        }
    }, []);

    const QUESTIONS_PER_PAGE = 10;

    const handleAnswerSelect = (questionId, answer) => {
        setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: answer,
        }));
    };

    const handlePageChange = (event, value) => {
        // Clear answers for the current page when switching pages
        const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
        const endIndex = startIndex + QUESTIONS_PER_PAGE;

        setSelectedAnswers((prev) => {
            const newAnswers = { ...prev };
            questions.slice(startIndex, endIndex).forEach((q) => {
                delete newAnswers[q.id];
            });
            return newAnswers;
        });

        setCurrentPage(value);
        // Scroll to the top of the page
        // window.scrollTo({ top: 0, behavior: 'smooth' }); // Adds smooth scrolling
        // Scroll to the top of the container
        // const container = document.querySelector('.MuiBox-root.css-23htwk');
        const mainElement = document.querySelector('#app-main'); // Locate the window where scroll works
        if (mainElement) {
            mainElement.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Fallback to window if container not found
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
    const endIndex = startIndex + QUESTIONS_PER_PAGE;
    const currentQuestions = questions.slice(startIndex, endIndex);

    return (
        <Container id="container-id" maxWidth="md">
            {/* <Box display="flex" justifyContent="space-between" mb={2}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    // onClick={() => navigate('/')} // Navigate to home page
                >
                    Back to Home
                </Button>
            </Box> */}

            {currentQuestions.map((question) => (
                <TestCard
                    key={question.id}
                    question={question}
                    onAnswerSelect={(answer) =>
                        handleAnswerSelect(question.id, answer)
                    }
                    selectedAnswer={selectedAnswers[question.id]}
                />
            ))}

            <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                    count={Math.ceil(questions.length / QUESTIONS_PER_PAGE)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>
        </Container>
    );
};

export default FragenPage;
